import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'
import {
    Camera,
    Loader2,
    Mail,
    PawPrint,
    PersonStandingIcon,
    Phone,
    Printer,
    Trash2,
    Users,
    X,
} from 'lucide-react'

import { useReservation } from '@/components/ReservationContext'
import { AdditionalServices } from '@/components/additional-services'
import { InvoiceModal } from '@/features/invoice/ui/InvoiceModal'
import { useAuth } from '@/shared/auth'
import { uploadFileToStorage } from '@/shared/fileStorage'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent } from '@/shared/ui/card'
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog'
import { DatePicker } from '@/shared/ui/date-picker'
import { BufferedNumberInput } from '@/shared/ui/buffered-number-input'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Separator } from '@/shared/ui/separator'
import { HairSalonReservation, PetSize } from '@monorepo/functions/src/types/reservations'
import { AdditionalService } from '@monorepo/functions/src/types/services'

import { findUnscheduledTasks } from '../model/task-utils'

interface HairSalonReservationModalProps {
    reservationId: string
    isOpen: boolean
    onClose: () => void
}

export function HairSalonReservationViewer({ reservationId, isOpen, onClose }: HairSalonReservationModalProps) {
    const { t } = useTranslation()
    const { reservation, updateReservation } = useReservation(reservationId) as {
        reservation: HairSalonReservation
        updateReservation: (updatedData: Partial<HairSalonReservation>) => Promise<void>
    }
    const [resultImage, setResultImage] = useState<File | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)

    if (!isOpen || !reservation) return null

    const { user } = useAuth()

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        try {
            setIsUploading(true)
            setResultImage(file)

            // Create local preview
            const localPreviewUrl = URL.createObjectURL(file)
            setPreviewUrl(localPreviewUrl)

            // Upload to Firebase Storage
            const filePath = `customers/${user?.uid}/hair-salon-results/${reservationId}`
            const downloadURL = await uploadFileToStorage(filePath, file, bytesTransferred => {
                const progress = (bytesTransferred / file.size) * 100
                setUploadProgress(progress)
            })

            // Update reservation with the download URL
            await updateReservation({ resultImage: downloadURL })

            // Clean up local preview URL
            URL.revokeObjectURL(localPreviewUrl)
            setPreviewUrl(null)
        } catch (error) {
            console.error('Error uploading image:', error)
            alert('Error al subir la imagen. Por favor, inténtalo de nuevo.')
            // Clean up on error
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl)
                setPreviewUrl(null)
            }
            setResultImage(null)
        } finally {
            setIsUploading(false)
            setUploadProgress(0)
        }
    }

    const handleDeleteReservation = () => {
        try {
            updateReservation({ status: 'cancelled' })
            setIsDeleteDialogOpen(false)
            onClose()
        } catch (error) {
            console.error('Error al eliminar la cita:', error)
            alert('Hubo un error al eliminar la cita. Por favor, inténtalo de nuevo.')
        }
    }

    const handleServiceChange = (newServices: AdditionalService[]) => {
        if (findUnscheduledTasks([{ ...reservation, additionalServices: newServices }])) {
            updateReservation({ status: 'pending', additionalServices: newServices })
        } else {
            updateReservation({ additionalServices: newServices })
        }
    }

    return (
        <div className='fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l bg-background shadow-xl'>
            <div className='flex items-center justify-between border-b px-6 py-4'>
                <div className='flex items-center gap-2'>
                    <h2 className='flex items-center gap-2 text-lg font-semibold'>
                        Detalles de la Cita
                        <Badge variant='outline' className={cn('ml-2', 'bg-violet-50 text-violet-700')}>
                            <div className='flex items-center gap-1.5'>
                                <Users className='h-3 w-3' />
                                Externo
                            </div>
                        </Badge>
                    </h2>
                </div>
                <div className='flex items-center gap-2'>
                    <Button
                        variant='outline'
                        size='icon'
                        onClick={() => setIsTicketModalOpen(true)}
                        title={t('reservation.viewer.printTicket', 'Imprimir ticket')}
                    >
                        <Printer className='h-4 w-4' />
                    </Button>
                    <Button variant='ghost' size='icon' onClick={onClose}>
                        <X className='h-5 w-5' />
                    </Button>
                </div>
            </div>
            <div className='flex-1 overflow-y-auto'>
                <div className='flex h-full'>
                    {/* Left Column - Services */}
                    <div className='w-1/3 p-6'>
                        <div className='space-y-6'>
                            <AdditionalServices
                                onServiceChange={handleServiceChange}
                                initialServices={reservation.additionalServices}
                                petCount={1}
                                petNames={[reservation.pet.name]}
                            />
                        </div>
                    </div>

                    {/* Right Column - Other Information */}
                    <div className='h-full w-2/3 space-y-8 border-l p-6'>
                        {/* Client and Pet Info */}
                        <div className='flex flex-row gap-4'>
                            {/* Client Card */}
                            <Card className='flex-grow'>
                                <CardContent className='flex w-full flex-col gap-4 pt-6'>
                                    <div className='flex items-center justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <PersonStandingIcon className='h-4 w-4' />
                                            Nombre:
                                        </span>
                                        <div className='flex items-center justify-end gap-2'>
                                            <Input
                                                value={reservation.client.name}
                                                onChange={e => {
                                                    updateReservation({
                                                        client: { ...reservation.client, name: e.target.value },
                                                    })
                                                }}
                                                className='w-fit'
                                            />
                                        </div>
                                    </div>
                                    <div className='flex items-center justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <Phone className='h-4 w-4' />
                                            Teléfono:
                                        </span>
                                        <div className='flex items-center justify-end gap-2'>
                                            <Input
                                                value={reservation.client.phone}
                                                onChange={e => {
                                                    updateReservation({
                                                        client: { ...reservation.client, phone: e.target.value },
                                                    })
                                                }}
                                                className='w-48'
                                            />
                                        </div>
                                    </div>
                                    <div className='flex items-center justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <Mail className='h-4 w-4' />
                                            Email:
                                        </span>
                                        <div className='flex items-center justify-end gap-2'>
                                            <Input
                                                value={reservation.client.email}
                                                onChange={e => {
                                                    updateReservation({
                                                        client: { ...reservation.client, email: e.target.value },
                                                    })
                                                }}
                                                className='w-48'
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Pet Card */}
                            <Card className='flex-grow'>
                                <CardContent className='flex w-full flex-col gap-4 pt-6'>
                                    <div className='flex items-center justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <PawPrint className='h-4 w-4' />
                                            Nombre:
                                        </span>
                                        <div className='flex items-center justify-end gap-2'>
                                            <Input
                                                value={reservation.pet.name}
                                                onChange={e => {
                                                    updateReservation({
                                                        pet: { ...reservation.pet, name: e.target.value },
                                                    })
                                                }}
                                                className='w-48'
                                            />
                                        </div>
                                    </div>
                                    <div className='flex items-center justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <PawPrint className='h-4 w-4' />
                                            Raza:
                                        </span>
                                        <div className='flex items-center justify-end gap-2'>
                                            <Input
                                                value={reservation.pet.breed}
                                                onChange={e => {
                                                    updateReservation({
                                                        pet: { ...reservation.pet, breed: e.target.value },
                                                    })
                                                }}
                                                className='w-48'
                                            />
                                        </div>
                                    </div>
                                    <div className='flex items-center justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <PawPrint className='h-4 w-4' />
                                            Tamaño:
                                        </span>
                                        <div className='flex items-center justify-end gap-2'>
                                            <Select
                                                value={reservation.pet.size}
                                                onValueChange={value => {
                                                    updateReservation({
                                                        pet: { ...reservation.pet, size: value as PetSize },
                                                    })
                                                }}
                                            >
                                                <SelectTrigger className='w-48'>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value='pequeño'>Pequeño</SelectItem>
                                                    <SelectItem value='mediano'>Mediano</SelectItem>
                                                    <SelectItem value='grande'>Grande</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className='flex items-center justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <PawPrint className='h-4 w-4' />
                                            Peso:
                                        </span>
                                        <div className='flex items-center justify-end gap-2'>
                                            <BufferedNumberInput
                                                key='pet-weight'
                                                initialValue={reservation.pet.weight ?? ''}
                                                onDebouncedChange={weight =>
                                                    updateReservation({
                                                        pet: { ...reservation.pet, weight },
                                                    })
                                                }
                                                className='w-48'
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <Separator />

                        {/* Fechas y Horarios */}
                        <div className='flex flex-row gap-6'>
                            {/* Left column: Date, Time, Price */}
                            <div className='flex flex-col gap-4'>
                                <Label>Fecha y hora</Label>
                                <DatePicker
                                    className='h-10 w-fit'
                                    date={new Date(reservation.date)}
                                    onSelect={date => {
                                        if (date) {
                                            updateReservation({ date: format(date, 'yyyy-MM-dd') })
                                        }
                                    }}
                                />
                                <Input
                                    type='time'
                                    className='w-fit'
                                    value={reservation.time}
                                    onChange={e => updateReservation({ time: e.target.value })}
                                />
                                <div className='space-y-2'>
                                    <Label htmlFor='price'>Precio (€)</Label>
                                    <BufferedNumberInput
                                        key='total-price'
                                        id='price'
                                        className='w-20'
                                        initialValue={reservation.totalPrice ?? ''}
                                        onDebouncedChange={totalPrice => updateReservation({ totalPrice })}
                                    />
                                </div>
                            </div>

                            {/* Right side: Image upload */}
                            <div className='space-y-2'>
                                {previewUrl || reservation.resultImage ? (
                                    <div className='relative max-w-full'>
                                        <img
                                            src={previewUrl || reservation.resultImage}
                                            alt='Preview'
                                            className='h-full w-full rounded-md object-cover'
                                        />
                                        {isUploading && (
                                            <div className='absolute inset-0 flex items-center justify-center rounded-md bg-black bg-opacity-50'>
                                                <div className='text-center text-white'>
                                                    <Loader2 className='mx-auto mb-2 h-6 w-6 animate-spin' />
                                                    <p className='text-sm'>Subiendo... {Math.round(uploadProgress)}%</p>
                                                </div>
                                            </div>
                                        )}
                                        <Button
                                            variant='destructive'
                                            size='icon'
                                            className='absolute right-2 top-2'
                                            disabled={isUploading}
                                            onClick={() => {
                                                setResultImage(null)
                                                setPreviewUrl(null)
                                                updateReservation({ resultImage: undefined })
                                            }}
                                        >
                                            <X className='h-4 w-4' />
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <Label>Foto del Resultado</Label>
                                        <div className='flex items-center gap-4'>
                                            <Button
                                                variant='outline'
                                                disabled={isUploading}
                                                onClick={() => document.getElementById('image-upload')?.click()}
                                                className='flex h-10 items-center gap-2'
                                            >
                                                {isUploading ? (
                                                    <Loader2 className='h-4 w-4 animate-spin' />
                                                ) : (
                                                    <Camera className='h-4 w-4' />
                                                )}
                                                {isUploading ? 'Subiendo...' : 'Subir Foto'}
                                            </Button>
                                            <input
                                                id='image-upload'
                                                type='file'
                                                accept='image/*'
                                                onChange={handleImageChange}
                                                disabled={isUploading}
                                                className='hidden'
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='flex justify-end gap-4 border-t px-6 py-4'>
                <Button
                    variant='destructive'
                    className='flex items-center gap-2'
                    disabled={reservation.status === 'cancelled'}
                    onClick={() => setIsDeleteDialogOpen(true)}
                >
                    <Trash2 className='h-4 w-4' />
                    Eliminar Cita
                </Button>
            </div>
            <ConfirmationDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDeleteReservation}
                title='Eliminar cita'
                description={`¿Estás seguro de que quieres eliminar esta cita de ${reservation.pet.name}? La cita se marcará como cancelada.`}
                confirmText='Eliminar'
                cancelText='Cancelar'
                variant='destructive'
            />
            {isTicketModalOpen && (
                <InvoiceModal
                    reservation={reservation}
                    onClose={() => setIsTicketModalOpen(false)}
                    onPaid={() => {}}
                    printOnly
                    isHairSalon
                />
            )}
        </div>
    )
}
